import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Tabs } from 'antd';
import PageHeader from '../components/UI/PageHeader/PageHeader';
import VehicleModels from '../components/Vehicles/VehicleSpecifications/vehicleModels';
import VehicleMakes from '../components/Vehicles/VehicleSpecifications/vehicleMakes';
import VehicleGenerations from '../components/Vehicles/VehicleSpecifications/vehicleGenerations';
import VehicleEquipments from '../components/Vehicles/VehicleSpecifications/vehicleEquipments';

import { fetchMakes } from '../store/actions/vehicleMakes-actions';
import { fetchModels } from '../store/actions/vehicleModels-actions';
import { fetchGenerations } from '../store/actions/vehicleGenerations-actions';
import { fetchEquipments } from '../store/actions/vehicleEquipments-actions';
import Spinner from '../components/UI/Spinner/Spinner';

const { TabPane } = Tabs;

const VehicleSpecifications = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const {
    makes,
    models,
    generations,
    equipments,
  } = useSelector((state) => ({
    makes: state.makes.makes,
    models: state.models.models,
    generations: state.generations.generations,
    equipments: state.equipments.equipments,
  }));

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const fetchPromises = [];
        
        // Check if each data set is empty before fetching
        if (!makes.length) fetchPromises.push(dispatch(fetchMakes(signal)));
        if (!models.length) fetchPromises.push(dispatch(fetchModels(signal)));
        if (!generations.length) fetchPromises.push(dispatch(fetchGenerations(signal)));
        if (!equipments.length) fetchPromises.push(dispatch(fetchEquipments(signal)));

        await Promise.all(fetchPromises);
      } catch (error) {
        if (error.name === 'AbortError') {
          console.log("Fetch aborted");
        } else {
          console.error("Error fetching data:", error);
        }
      } finally {
        setIsLoading(false);
      }
    };

    // Fetch data only if any of the data sets are empty
    if (!makes.length || !models.length || !generations.length || !equipments.length) {
      fetchData();
    } else {
      setIsLoading(false); // Set loading to false if data already exists
    }

    // Cleanup function to abort fetch requests on unmount
    return () => {
      controller.abort();
    };
  }, [dispatch, makes.length, models.length, generations.length, equipments.length]);

  return (
    <>
      <PageHeader title={t('navigation.vehicleSpecifications')} />
      {isLoading ? (
        <Spinner />
      ) : (
        <Tabs defaultActiveKey="1">
          <TabPane tab={t('vehicles.makes')} key="1">
            <VehicleMakes makes={makes} />
          </TabPane>
          <TabPane tab={t('vehicles.models')} key="2">
            <VehicleModels models={models} makes={makes} />
          </TabPane>
          <TabPane tab={t('vehicles.generations')} key="3">
            <VehicleGenerations generations={generations} models={models} />
          </TabPane>
          <TabPane tab={t('vehicles.equipments')} key="4">
            <VehicleEquipments equipments={equipments} generations={generations} />
          </TabPane>
        </Tabs>
      )}
    </>
  );
};

export default VehicleSpecifications;
